import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiArrowLeft, FiBox, FiMapPin, FiCalendar, FiShield, FiHash,
  FiTool, FiTrash2, FiAlertCircle, FiMessageSquare, FiPaperclip, FiCheckCircle,
  FiFile, FiPlus, FiX, FiSend, FiChevronDown, FiUpload, FiImage, FiVideo,
  FiEdit2, FiPrinter, FiKey, FiClock, FiDollarSign, FiCornerDownLeft, FiArchive
} from 'react-icons/fi';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../context/ThemeContext';
import QRLabelModal from '../components/QRLabelModal';
import WorkOrderCreateModal from '../components/WorkOrderCreateModal';
import UploadProgress from '../components/UploadProgress';
import FunctionalLocationPicker from '../components/FunctionalLocationPicker';
import { inputStyle } from '../utils/constants';
import titleCase from '../utils/titleCase';
import capitalizeFirst from '../utils/capitalizeFirst';

const statusColors = {
  'New': { bg: 'rgba(217,119,6,0.1)', color: '#d97706', border: 'rgba(217,119,6,0.3)' },
  'In Progress': { bg: 'rgba(37,99,235,0.1)', color: '#2563eb', border: 'rgba(37,99,235,0.3)' },
  'Complete': { bg: 'rgba(22,163,74,0.1)', color: '#16a34a', border: 'rgba(22,163,74,0.3)' },
  'TECO': { bg: 'rgba(6,95,70,0.1)', color: '#065f46', border: 'rgba(6,95,70,0.3)' },
};
const priorityColors = { 'High': '#dc2626', 'Normal': '#64748b' };

function formatDateAU(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<\/div>/gi, '\n').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\u00a0/g, ' ').replace(/[^\S\n]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
};

// Make a one-paragraph notes blob easier to scan: keep any author-supplied
// newlines, but if the text is one long line, split after sentence
// terminators (. ! ?) when followed by a capital letter so each sentence
// lands on its own visual line. Avoids wrecking abbreviations like "e.g."
// because the lookahead requires an uppercase letter after the space.
const formatNotes = (s) => {
  const t = String(s || '');
  if (!t.trim()) return '';
  if (/\n/.test(t)) return t;
  return t.replace(/([.!?])\s+(?=[A-Z])/g, '$1\n');
};

function FilePickerButton({ files, setFiles, maxFiles = 8 }) {
  const fileRef = useRef();
  const remaining = maxFiles - files.length;

  const handlePick = (e) => {
    const picked = Array.from(e.target.files).slice(0, remaining);
    setFiles(prev => [...prev, ...picked].slice(0, maxFiles));
    e.target.value = '';
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  return (
    <div>
      <input ref={fileRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar"
        onChange={handlePick} style={{ display: 'none' }} />
      <motion.button type="button" whileTap={{ scale: 0.95 }}
        onClick={() => remaining > 0 && fileRef.current.click()}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
          border: '1px dashed var(--border)', background: 'var(--bg-input)', color: 'var(--text-secondary)',
          cursor: remaining > 0 ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 500,
          opacity: remaining > 0 ? 1 : 0.5
        }}>
        <FiUpload size={14} /> Browse Files ({files.length}/{maxFiles})
      </motion.button>
      {files.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {files.map((f, i) => {
            const isImg = f.type?.startsWith('image/');
            const isVid = f.type?.startsWith('video/');
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8,
                background: isImg ? 'rgba(37,99,235,0.06)' : isVid ? 'rgba(14,165,233,0.06)' : 'var(--bg-input)',
                border: '1px solid var(--border)', fontSize: 11, color: 'var(--text-secondary)', maxWidth: 180
              }}>
                {isImg ? <FiImage size={12} color="var(--accent)" /> : isVid ? <FiVideo size={12} color="#0ea5e9" /> : <FiFile size={12} />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8', flexShrink: 0 }}>
                  <FiX size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ImageLightbox({ src, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, cursor: 'zoom-out' }}>
      <motion.img initial={{ scale: 0.7 }} animate={{ scale: 1 }} exit={{ scale: 0.7 }} src={src} alt="Attachment"
        style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 25px 50px rgba(0,0,0,0.5)', objectFit: 'contain' }} />
      <button onClick={onClose} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 40, height: 40, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}><FiX /></button>
    </motion.div>
  );
}

function FileAttachments({ files, onLightbox, token }) {
  if (!files || files.length === 0) return null;
  const images = files.filter(f => f.isImage);
  const videos = files.filter(f => f.isVideo);
  const others = files.filter(f => !f.isImage && !f.isVideo);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getFileIcon = (ext) => {
    if (['.pdf'].includes(ext)) return { icon: FiFile, color: '#dc2626', label: 'PDF' };
    if (['.doc', '.docx'].includes(ext)) return { icon: FiFile, color: '#2563eb', label: 'DOC' };
    if (['.xls', '.xlsx', '.csv'].includes(ext)) return { icon: FiFile, color: '#16a34a', label: 'XLS' };
    return { icon: FiFile, color: 'var(--text-secondary)', label: ext?.replace('.', '').toUpperCase() || 'FILE' };
  };

  return (
    <div style={{ marginTop: 8 }}>
      {images.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
          {images.map(f => (
            <motion.div key={f.id} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={() => onLightbox(f.url + `?token=${token}`)}
              style={{ cursor: 'zoom-in', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg-card)', boxShadow: 'var(--shadow)' }}>
              <div style={{ aspectRatio: '4/3', overflow: 'hidden', position: 'relative' }}>
                <img src={f.url + `?token=${token}`} alt={f.file} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', top: 6, right: 6, padding: '2px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, fontWeight: 700 }}>
                  <FiImage size={9} style={{ marginRight: 3 }} />{f.ext?.replace('.', '').toUpperCase()}
                </div>
              </div>
              <div style={{ padding: '8px 10px' }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file}</div>
                {f.created_at && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{formatDate(f.created_at)}</div>}
              </div>
            </motion.div>
          ))}
        </div>
      )}
      {videos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
          {videos.map(f => (
            <div key={f.id} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg-card)', boxShadow: 'var(--shadow)' }}>
              <video controls preload="metadata" style={{ width: '100%', maxHeight: 360, display: 'block' }}>
                <source src={f.url + `?token=${token}`} />
              </video>
              <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiVideo size={14} color="#0ea5e9" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file}</div>
                  {f.created_at && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(f.created_at)}</div>}
                </div>
                <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: 'rgba(14,165,233,0.08)', color: '#0ea5e9' }}>Video</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {others.map(f => {
            const fi = getFileIcon(f.ext);
            return (
              <a key={f.id} href={f.url + `?token=${token}`} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', textDecoration: 'none', boxShadow: 'var(--shadow)', transition: 'border-color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = fi.color}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: `${fi.color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <fi.icon size={16} color={fi.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file}</div>
                  {f.created_at && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(f.created_at)}</div>}
                </div>
                <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: `${fi.color}10`, color: fi.color }}>{fi.label}</span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function IssueCard({ issue, authFetch, onDelete, token }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadedDetail, setLoadedDetail] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [sendingComment, setSendingComment] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileList, setUploadFileList] = useState([]);
  const issueFileRef = useRef();
  const sc = statusColors[issue.status] || statusColors['New'];

  const loadDetail = async () => {
    if (loadedDetail) return;
    const [cr, fr] = await Promise.all([authFetch(`/api/issues/${issue.id}/comments`), authFetch(`/api/issues/${issue.id}/files`)]);
    setComments(await cr.json()); setFiles(await fr.json()); setLoadedDetail(true);
  };

  const toggleExpand = () => { if (!expanded) loadDetail(); setExpanded(!expanded); };

  const addComment = async () => {
    if (!newComment.trim() && commentFiles.length === 0) return;
    setSendingComment(true);
    const formData = new FormData();
    formData.append('comment', newComment);
    commentFiles.forEach(f => formData.append('files', f));

    const res = await authFetch(`/api/issues/${issue.id}/comments`, { method: 'POST', body: formData });
    if (res.ok) { const c = await res.json(); setComments(prev => [...prev, c]); setNewComment(''); setCommentFiles([]); }
    setSendingComment(false);
  };

  const uploadIssueFiles = async (e) => {
    const picked = Array.from(e.target.files).slice(0, 8);
    if (picked.length === 0) return;
    setUploadingFiles(true);
    setUploadProgress(0);
    setUploadFileList(picked);
    const formData = new FormData();
    picked.forEach(f => formData.append('files', f));
    try {
      const newFiles = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/issues/${issue.id}/files`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100)); };
        xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText)); else reject(); };
        xhr.onerror = () => reject();
        xhr.send(formData);
      });
      setFiles(prev => [...prev, ...newFiles]);
    } catch (err) { /* upload failed */ }
    setUploadingFiles(false);
    setUploadFileList([]);
    e.target.value = '';
  };

  return (
    <motion.div layout initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20, height: 0 }}
      style={{ background: 'var(--bg-card)', borderRadius: 14, overflow: 'hidden', border: `1px solid ${expanded ? sc.border : 'var(--border)'}`, transition: 'border-color 0.2s', boxShadow: 'var(--shadow)' }}>
      <AnimatePresence>
        {lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
      </AnimatePresence>

      <div onClick={toggleExpand} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'} onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.5px', fontFamily: 'monospace' }}>{issue.wo_number || `WO-${issue.id}`}{issue.first_operation ? `/${issue.first_operation}` : ''}</span>
            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color }}>{issue.status}</span>
            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(148,163,184,0.1)', color: priorityColors[issue.priority] || '#64748b' }}>{issue.priority}</span>
            <span style={{ padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, color: '#fff', background: {'PM01':'#2563eb','PM02':'#0ea5e9','PM03':'#f59e0b'}[issue.type] || '#6b7280' }}>{issue.type}</span>
            {issue.comment_count > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-muted)' }}><FiMessageSquare size={11} />{issue.comment_count}</span>}
            {issue.file_count > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--text-muted)' }}><FiPaperclip size={11} />{issue.file_count}</span>}
            {issue.total_minutes > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#2563eb', fontWeight: 600 }}><FiClock size={11} />{Math.floor(issue.total_minutes / 60)}h {issue.total_minutes % 60}m</span>}
          </div>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{issue.name}</h4>
          {issue.due_date && issue.due_date !== '' && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Due: {(() => { const d = new Date(issue.due_date); return isNaN(d.getTime()) ? issue.due_date : `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</div>}
        </div>
        <motion.div animate={{ rotate: expanded ? 180 : 0 }} style={{ color: 'var(--text-muted)', flexShrink: 0 }}><FiChevronDown size={18} /></motion.div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--border)' }}>
              {issue.description && stripHtml(issue.description) && (
                <div style={{ marginTop: 16, padding: '14px', borderRadius: 10, background: 'var(--bg-input)', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap', border: '1px solid var(--border)' }}>
                  {stripHtml(issue.description)}
                </div>
              )}

              {/* Attachments */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiPaperclip size={13} /> Attachments ({files.length})
                  <input ref={issueFileRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar" onChange={uploadIssueFiles} style={{ display: 'none' }} />
                  <motion.button whileTap={{ scale: 0.95 }} onClick={() => issueFileRef.current.click()} disabled={uploadingFiles}
                    style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: '1px dashed var(--border)', background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                    <FiUpload size={11} /> {uploadingFiles ? 'Uploading...' : 'Upload (max 8)'}
                  </motion.button>
                </div>
                {uploadingFiles && <UploadProgress files={uploadFileList} progress={uploadProgress} uploading={true} />}
                <FileAttachments files={files} onLightbox={setLightboxSrc} token={token} />
                {files.length === 0 && !uploadingFiles && <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No attachments</div>}
              </div>

              {/* Comments */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><FiMessageSquare size={13} /> Comments ({comments.length})</div>
                {comments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, maxHeight: 400, overflowY: 'auto' }}>
                    {comments.map((c, idx) => (
                      <motion.div key={c.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                        style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--bg-input)', borderLeft: '3px solid var(--accent)', border: '1px solid var(--border)', borderLeftWidth: 3, borderLeftColor: 'var(--accent)' }}>
                        {c.comment && <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.comment}</div>}
                        <FileAttachments files={c.files || []} onLightbox={setLightboxSrc} token={token} />
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>{new Date(c.created_at).toLocaleString()}</div>
                      </motion.div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment..."
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && addComment()}
                      style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
                    <motion.button whileTap={{ scale: 0.9 }} onClick={addComment} disabled={sendingComment}
                      style={{ padding: '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center' }}><FiSend size={14} /></motion.button>
                  </div>
                  <FilePickerButton files={commentFiles} setFiles={setCommentFiles} />
                </div>
              </div>

              {/* Delete */}
              <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                {deleteConfirm ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiAlertCircle size={16} color="#dc2626" /><span style={{ fontSize: 13, color: '#dc2626', fontWeight: 500 }}>Delete this work order?</span>
                    <motion.button whileTap={{ scale: 0.95 }} onClick={() => onDelete(issue.id)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', marginLeft: 8 }}>Yes, Delete</motion.button>
                    <motion.button whileTap={{ scale: 0.95 }} onClick={() => setDeleteConfirm(false)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</motion.button>
                  </div>
                ) : (
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setDeleteConfirm(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)', color: '#dc2626', cursor: 'pointer' }}>
                    <FiTrash2 size={13} /> Delete Work Order</motion.button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const editInputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' };

function EditAssetModal({ asset, lookups, onClose, onSave }) {
  const { authFetch } = useAuth();
  const todayStr = new Date().toISOString().split('T')[0];
  const cfDefs = lookups.customFields || [];
  const existingCfv = (() => { try { return JSON.parse(asset.custom_fields_values || '{}'); } catch { return {}; } })();
  const [form, setForm] = useState({
    tag: asset.tag || '', name: asset.name || '', serial_number: asset.serial_number || '',
    purchase_date: asset.purchase_date && asset.purchase_date !== '1970-01-01' ? asset.purchase_date.split('T')[0] : '',
    warranty_end: asset.warranty_end && asset.warranty_end !== '1970-01-01' ? asset.warranty_end.split('T')[0] : '',
    notes: asset.notes || '',
    status_id: asset.status_id || '',
    manufacturer_id: asset.manufacturer_id || '', model_id: asset.model_id || '',
    supplier_name: asset.supplier_name || '',
    functional_location_id: asset.functional_location_id || '',
    parent_asset_id: asset.parent_asset_id || '',
  });
  // Search-as-you-type list of assets for the parent picker. Loaded lazily
  // the first time the picker opens.
  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState([]);
  const [parentPickerOpen, setParentPickerOpen] = useState(false);
  const [parentLabel, setParentLabel] = useState(asset.parent_asset ? `${asset.parent_asset.tag} — ${asset.parent_asset.name}` : '');
  const [cfValues, setCfValues] = useState(existingCfv);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try { await onSave({ ...form, custom_fields_values: JSON.stringify(cfValues) }); onClose(); } catch (err) { setError(err.message); }
    setSaving(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <motion.div initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
        style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 560, border: '1px solid var(--border)', maxHeight: '90vh', boxShadow: 'var(--shadow-xl)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Edit Asset</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><FiX size={20} /></button>
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}><FiAlertCircle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Asset ID</label>
              <input value={form.tag} readOnly placeholder="Auto-assigned" style={{ ...editInputStyle, color: 'var(--text-secondary)', cursor: 'not-allowed' }} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Name *</label>
              <input value={form.name} onChange={e => set('name', titleCase(e.target.value))} required placeholder="Asset name" style={editInputStyle} /></div>
          </div>
          <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Serial Number</label>
            <input value={form.serial_number} onChange={e => set('serial_number', e.target.value)} placeholder="Serial number" style={editInputStyle} /></div>
          <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Status</label>
            <select value={form.status_id} onChange={e => set('status_id', e.target.value)} style={editInputStyle}><option value="">None</option>
              {lookups.statuses?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          {Array.isArray(lookups.functionalLocations) && lookups.functionalLocations.length > 0 && (
            <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Functional Location</label>
              <FunctionalLocationPicker nodes={lookups.functionalLocations} value={form.functional_location_id} onChange={v => set('functional_location_id', v)} style={editInputStyle} placeholder="None (unattached)" />
            </div>
          )}
          {/* Parent asset picker — make this asset a sub-asset of another.
              Search-as-you-type combobox; results from the assets list endpoint
              capped at 30 hits. Selecting an asset auto-inherits its FL (the
              server does the inherit; the client just sends parent_asset_id). */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Parent Asset (optional)</label>
            {form.parent_asset_id ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'rgba(139,92,246,0.08)' }}>
                <FiBox size={13} style={{ color: '#8b5cf6' }} />
                <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>{parentLabel || `Asset #${form.parent_asset_id}`}</span>
                <button type="button" onClick={() => { set('parent_asset_id', ''); setParentLabel(''); setParentSearch(''); }}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11 }}>Clear</button>
              </div>
            ) : (
              <input value={parentSearch} onFocus={() => setParentPickerOpen(true)}
                onChange={e => { const q = e.target.value; setParentSearch(q); setParentPickerOpen(true);
                  // Fetch matching assets — top-level + children alike — bounded to 30.
                  authFetch(`/api/assets?search=${encodeURIComponent(q)}&limit=30`).then(r => r.ok ? r.json() : { data: [] })
                    .then(d => setParentResults((d.data || []).filter(a => a.id !== asset.id)))
                    .catch(() => setParentResults([]));
                }}
                placeholder="Search by Asset ID or name…"
                style={editInputStyle} />
            )}
            {parentPickerOpen && !form.parent_asset_id && parentResults.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, maxHeight: 240, overflow: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                {parentResults.slice(0, 30).map(a => (
                  <div key={a.id}
                    onMouseDown={e => { e.preventDefault(); set('parent_asset_id', a.id); setParentLabel(`${a.tag} — ${a.name}`); setParentPickerOpen(false); setParentSearch(''); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#8b5cf6' }}>{a.tag}</span>
                    <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>{a.name}</span>
                    {a.functional_location_code && <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--text-muted)' }}>({a.functional_location_code})</span>}
                  </div>
                ))}
              </div>
            )}
            {form.parent_asset_id && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Inherits the parent's functional location unless you override above.
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Manufacturer</label>
              <select value={form.manufacturer_id} onChange={e => set('manufacturer_id', e.target.value)} style={editInputStyle}><option value="">None</option>
                {lookups.manufacturers?.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Model</label>
              <select value={form.model_id} onChange={e => set('model_id', e.target.value)} style={editInputStyle}><option value="">None</option>
                {lookups.models?.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
          </div>
          <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Supplier</label>
            <input value={form.supplier_name || ''} onChange={e => set('supplier_name', titleCase(e.target.value))} placeholder="Optional — e.g. Acme Industrial" style={editInputStyle} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Purchase Date</label>
              <input type="date" value={form.purchase_date} max={todayStr} onChange={e => set('purchase_date', e.target.value)} style={editInputStyle} /></div>
            <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Warranty End</label>
              <input type="date" value={form.warranty_end} min={todayStr} onChange={e => set('warranty_end', e.target.value)} style={editInputStyle} /></div>
          </div>
          <div><label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Additional notes..." style={{ ...editInputStyle, resize: 'vertical', fontFamily: 'inherit' }} /></div>
          {cfDefs.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4, paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>Custom Fields</div>
              <div style={{ display: 'grid', gridTemplateColumns: cfDefs.length === 1 ? '1fr' : '1fr 1fr', gap: 12 }}>
                {cfDefs.map(cf => (
                  <div key={cf.id}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>{cf.name}</label>
                    <input
                      type={cf.field_type === 'number' ? 'number' : cf.field_type === 'date' ? 'date' : 'text'}
                      value={cfValues[cf.id] || ''}
                      onChange={e => {
                        const val = cf.field_type === 'text' ? titleCase(e.target.value) : e.target.value;
                        setCfValues(prev => ({ ...prev, [cf.id]: val }));
                      }}
                      placeholder={cf.name}
                      style={editInputStyle}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
          <div style={{ display: 'flex', gap: 10, padding: '16px 28px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
            <button type="button" onClick={onClose}
              style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
              Cancel
            </button>
            <motion.button type="submit" disabled={saving} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'var(--accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.7 : 1, boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
              {saving ? 'Saving...' : 'Update Asset'}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function AssetFilesTab({ assetId, authFetch, token }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    authFetch(`/api/assets/${assetId}/files`).then(r => r.ok ? r.json() : []).then(data => { setFiles(Array.isArray(data) ? data : []); setLoading(false); }).catch(() => { setFiles([]); setLoading(false); });
  }, [assetId]);

  const uploadFiles = async (e) => {
    const picked = Array.from(e.target.files).slice(0, 8);
    if (picked.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadFileList(picked);
    const formData = new FormData();
    picked.forEach(f => formData.append('files', f));
    try {
      const newFiles = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/assets/${assetId}/files`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100)); };
        xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText)); else reject(); };
        xhr.onerror = () => reject();
        xhr.send(formData);
      });
      setFiles(prev => [...newFiles, ...prev]);
    } catch (err) { /* upload failed */ }
    setUploading(false);
    setUploadFileList([]);
    e.target.value = '';
  };

  const deleteFile = async (fileId) => {
    await authFetch(`/api/assets/file/${fileId}`, { method: 'DELETE' });
    setFiles(prev => prev.filter(f => f.id !== fileId));
    setDeleteConfirm(null);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const getFileIcon = (ext) => {
    if (['.pdf'].includes(ext)) return { icon: FiFile, color: '#dc2626', label: 'PDF' };
    if (['.doc', '.docx'].includes(ext)) return { icon: FiFile, color: '#2563eb', label: 'DOC' };
    if (['.xls', '.xlsx', '.csv'].includes(ext)) return { icon: FiFile, color: '#16a34a', label: 'XLS' };
    return { icon: FiFile, color: 'var(--text-secondary)', label: ext?.replace('.', '').toUpperCase() || 'FILE' };
  };

  if (loading) return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, border: '1px solid var(--border)' }}>
      <div className="skeleton" style={{ width: 140, height: 18, marginBottom: 16 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
        {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 160, borderRadius: 10 }} />)}
      </div>
    </div>
  );

  const images = files.filter(f => f.isImage);
  const videos = files.filter(f => f.isVideo);
  const others = files.filter(f => !f.isImage && !f.isVideo);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
      <AnimatePresence>{lightboxSrc && <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}</AnimatePresence>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Documents & Attachments ({files.length})</h3>
        <input ref={fileRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.rar" onChange={uploadFiles} style={{ display: 'none' }} />
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => fileRef.current.click()} disabled={uploading}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(37,99,235,0.3)', opacity: uploading ? 0.7 : 1 }}>
          <FiUpload size={16} /> {uploading ? 'Uploading...' : 'Upload Documents'}
        </motion.button>
      </div>

      {uploading && <UploadProgress files={uploadFileList} progress={uploadProgress} uploading={true} />}

      {files.length === 0 && !uploading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          <FiPaperclip size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontSize: 14 }}>No documents attached to this asset</p>
          <p style={{ fontSize: 12, marginTop: 4 }}>Upload images, videos, documents and more</p>
        </div>
      ) : (
        <div>
          {images.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><FiImage size={13} /> Images ({images.length})</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                {images.map(f => (
                  <div key={f.id} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg-card)', boxShadow: 'var(--shadow)', position: 'relative' }}>
                    <motion.div whileHover={{ scale: 1.03 }} onClick={() => setLightboxSrc(f.url + `?token=${token}`)}
                      style={{ cursor: 'zoom-in', aspectRatio: '4/3', overflow: 'hidden' }}>
                      <img src={f.url + `?token=${token}`} alt={f.original_name || f.file} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </motion.div>
                    <div style={{ padding: '8px 10px' }}>
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name || f.file}</div>
                      {f.created_at && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{formatDate(f.created_at)}</div>}
                      {deleteConfirm === f.id ? (
                        <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                          <button onClick={() => deleteFile(f.id)} style={{ flex: 1, padding: '4px', borderRadius: 4, fontSize: 10, fontWeight: 600, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Delete</button>
                          <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '4px', borderRadius: 4, fontSize: 10, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirm(f.id)} style={{ marginTop: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 10, padding: 0 }}
                          onMouseEnter={e => e.target.style.color = '#dc2626'} onMouseLeave={e => e.target.style.color = 'var(--text-muted)'}><FiTrash2 size={10} /> Remove</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {videos.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><FiVideo size={13} /> Videos ({videos.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                {videos.map(f => (
                  <div key={f.id} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg-card)', boxShadow: 'var(--shadow)' }}>
                    <video controls preload="metadata" style={{ width: '100%', maxHeight: 360, display: 'block' }}>
                      <source src={f.url + `?token=${token}`} />
                    </video>
                    <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name || f.file}</div>
                        {f.created_at && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(f.created_at)}</div>}
                      </div>
                      {deleteConfirm === f.id ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => deleteFile(f.id)} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Delete</button>
                          <button onClick={() => setDeleteConfirm(null)} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirm(f.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11 }}
                          onMouseEnter={e => e.target.style.color = '#dc2626'} onMouseLeave={e => e.target.style.color = 'var(--text-muted)'}><FiTrash2 size={11} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {others.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><FiFile size={13} /> Documents ({others.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {others.map(f => {
                  const fi = getFileIcon(f.ext);
                  return (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: `${fi.color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <fi.icon size={16} color={fi.color} />
                      </div>
                      <a href={f.url + `?token=${token}`} target="_blank" rel="noopener noreferrer" style={{ flex: 1, minWidth: 0, textDecoration: 'none' }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name || f.file}</div>
                        {f.created_at && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(f.created_at)}</div>}
                      </a>
                      <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: `${fi.color}10`, color: fi.color }}>{fi.label}</span>
                      {deleteConfirm === f.id ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => deleteFile(f.id)} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Delete</button>
                          <button onClick={() => setDeleteConfirm(null)} style={{ padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirm(f.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 11 }}
                          onMouseEnter={e => e.target.style.color = '#dc2626'} onMouseLeave={e => e.target.style.color = 'var(--text-muted)'}><FiTrash2 size={11} /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function AssetDetail() {
  const { id } = useParams(); const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromQR = searchParams.get('from') === 'qr';
  const { authFetch, token } = useAuth();
  const { isDark } = useTheme();
  const [checkins, setCheckins] = useState([]);
  const [qrBannerVisible, setQrBannerVisible] = useState(fromQR);
  const [asset, setAsset] = useState(null); const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true); const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [showEditModal, setShowEditModal] = useState(false);
  const [lookups, setLookups] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [assetTotalTime, setAssetTotalTime] = useState(null);
  const [assetCosts, setAssetCosts] = useState(null);
  const [assetUtilization, setAssetUtilization] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [newLicense, setNewLicense] = useState({ name: '', vendor: '', license_key: '' });
  const [editingLicense, setEditingLicense] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState(new Set());
  const [woTypeFilter, setWoTypeFilter] = useState('All');
  const [woSearch, setWoSearch] = useState('');
  const [assetFileCount, setAssetFileCount] = useState(0);
  // Direct sub-assets of this asset (caravan → generator + pump + awning).
  const [childAssets, setChildAssets] = useState([]);

  const loadData = useCallback(() => {
    Promise.all([
      authFetch(`/api/assets/${id}`).then(r => r.json()),
      authFetch(`/api/issues/asset/${id}`).then(r => r.json()),
      authFetch(`/api/issues/asset/${id}/total-time`).then(r => r.ok ? r.json() : null).catch(() => null),
      authFetch(`/api/issues/asset/${id}/costs`).then(r => r.ok ? r.json() : null).catch(() => null),
      authFetch(`/api/assets/${id}/licenses`).then(r => r.ok ? r.json() : []).catch(() => []),
      authFetch(`/api/assets/${id}/files`).then(r => r.ok ? r.json() : []).catch(() => []),
      authFetch(`/api/issues/asset/${id}/utilization`).then(r => r.ok ? r.json() : null).catch(() => null)
    ]).then(([a, i, t, c, l, f, u]) => { setAsset(a); setIssues(i); setAssetTotalTime(t); setAssetCosts(c); setLicenses(l); setAssetFileCount(Array.isArray(f) ? f.length : 0); setAssetUtilization(u); setLoading(false); });
    // Sub-assets fetch is cheap and independent. Skipped silently on
    // pre-v105 tenants (route returns [] when the column is missing).
    authFetch(`/api/assets/${id}/children`).then(r => r.ok ? r.json() : []).then(d => setChildAssets(Array.isArray(d) ? d : [])).catch(() => setChildAssets([]));
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Log QR check-in when arriving via QR scan, then load recent check-ins
  useEffect(() => {
    if (fromQR && id) {
      authFetch(`/api/issues/asset/${id}/checkin`, { method: 'POST', body: JSON.stringify({ source: 'qr' }) })
        .catch(() => {})
        .finally(() => {
          authFetch(`/api/issues/asset/${id}/checkins`).then(r => r.ok ? r.json() : []).then(d => setCheckins(Array.isArray(d) ? d : [])).catch(() => {});
        });
    } else if (id) {
      authFetch(`/api/issues/asset/${id}/checkins`).then(r => r.ok ? r.json() : []).then(d => setCheckins(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [id, fromQR]);
  useEffect(() => { authFetch('/api/assets/lookups').then(r => r.json()).then(setLookups); }, []);

  const editAsset = async (form) => {
    const res = await authFetch(`/api/assets/${id}`, { method: 'PUT', body: JSON.stringify(form) });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    loadData();
  };

  const deleteAsset = async () => {
    await authFetch(`/api/assets/${id}`, { method: 'DELETE' });
    navigate('/assets');
  };

  const deleteIssue = async (issueId) => {
    await authFetch(`/api/issues/${issueId}`, { method: 'DELETE' });
    setIssues(prev => prev.filter(i => i.id !== issueId));
  };
  const onCreated = (n) => setIssues(prev => [n, ...prev]);

  const addLicense = async () => {
    if (!newLicense.name.trim()) return;
    const res = await authFetch(`/api/assets/${id}/licenses`, { method: 'POST', body: JSON.stringify(newLicense) });
    if (res.ok) {
      const lic = await res.json();
      setLicenses(prev => [lic, ...prev]);
      setNewLicense({ name: '', vendor: '', license_key: '' });
    }
  };

  const updateLicense = async (licId) => {
    if (!editingLicense?.name?.trim()) return;
    const res = await authFetch(`/api/assets/license/${licId}`, { method: 'PUT', body: JSON.stringify(editingLicense) });
    if (res.ok) {
      const updated = await res.json();
      setLicenses(prev => prev.map(l => l.id === licId ? updated : l));
      setEditingLicense(null);
    }
  };

  const deleteLicense = async (licId) => {
    await authFetch(`/api/assets/license/${licId}`, { method: 'DELETE' });
    setLicenses(prev => prev.filter(l => l.id !== licId));
  };

  const maskKey = (key) => {
    if (!key) return '';
    if (key.length <= 4) return '****';
    return '****' + key.slice(-4);
  };

  const parseCustomFields = (json) => {
    try {
      const obj = JSON.parse(json);
      const cfDefs = lookups.customFields || [];
      const labels = {};
      cfDefs.forEach(cf => { labels[String(cf.id)] = cf.name; });
      return Object.entries(obj).filter(([_, v]) => v && String(v).trim() !== '' && String(v).trim() !== 'N/A').map(([k, v]) => ({ label: labels[k] || `Field ${k}`, value: String(v).trim() }));
    } catch { return []; }
  };

  if (loading) return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div className="skeleton" style={{ width: 120, height: 16, marginBottom: 16 }} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="skeleton" style={{ width: 70, height: 26, borderRadius: 8 }} />
          <div className="skeleton" style={{ width: 200, height: 28 }} />
        </div>
      </div>
      <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 24, border: '1px solid var(--border)', marginBottom: 24 }}>
        <div className="skeleton" style={{ width: 160, height: 18, marginBottom: 16 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ display: 'flex', gap: 10 }}>
              <div className="skeleton" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }} />
              <div><div className="skeleton" style={{ width: 60, height: 12, marginBottom: 4 }} /><div className="skeleton" style={{ width: 100, height: 14 }} /></div>
            </div>
          ))}
        </div>
      </div>
      <div className="skeleton" style={{ width: 180, height: 22, marginBottom: 16 }} />
      {[...Array(3)].map((_, i) => (
        <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 14, padding: 20, border: '1px solid var(--border)', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div className="skeleton" style={{ width: 60, height: 22, borderRadius: 20 }} />
            <div className="skeleton" style={{ width: 50, height: 22, borderRadius: 20 }} />
          </div>
          <div className="skeleton" style={{ width: '60%', height: 16 }} />
        </div>
      ))}
    </div>
  );

  if (!asset) return <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Asset not found</div>;
  const customFields = parseCustomFields(asset.custom_fields_values);

  return (
    <div>
      <AnimatePresence>
        {showCreateModal && <WorkOrderCreateModal preselectedAssetId={asset.id} onClose={() => setShowCreateModal(false)} onCreated={(result) => { setShowCreateModal(false); onCreated(result); }} />}
        {showQR && <QRLabelModal asset={asset} onClose={() => setShowQR(false)} />}
        {showEditModal && <EditAssetModal asset={asset} lookups={lookups} onClose={() => setShowEditModal(false)} onSave={editAsset} />}
      </AnimatePresence>

      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 24 }}>
        <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500, marginBottom: 16, padding: 0 }}><FiArrowLeft size={16} /> Back</button>
        {/* Parent-asset breadcrumb: show 'sub-asset of …' when this asset has a parent */}
        {asset.parent_asset && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            <FiCornerDownLeft size={11} style={{ transform: 'rotate(180deg)' }} />
            <span>Sub-asset of</span>
            <button onClick={() => navigate(`/assets/${asset.parent_asset.id}`)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#8b5cf6', fontSize: 12, fontWeight: 700 }}>
              <span style={{ fontFamily: 'monospace' }}>{asset.parent_asset.tag}</span>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>— {asset.parent_asset.name}</span>
            </button>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, background: 'rgba(37,99,235,0.08)', color: 'var(--accent)', flexShrink: 0 }}>{asset.tag}</span>
          <h1 title={asset.name} style={{ fontSize: 'clamp(18px, 5vw, 28px)', fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text-primary)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{asset.name}</h1>
          {asset.status_name && <span style={{ padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: `${asset.status_color}15`, color: asset.status_color }}>{asset.status_name}</span>}
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowEditModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <FiEdit2 size={14} /> Edit
          </motion.button>
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowQR(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <FiPrinter size={14} /> QR
          </motion.button>
        </div>
      </motion.div>

      {/* Redundant banner — new WOs are blocked until restored. status_id 4 = Redundant. */}
      {Number(asset.status_id) === 4 && (
        <div style={{ marginBottom: 16, padding: '14px 18px', borderRadius: 12, background: 'rgba(122,121,130,0.08)', border: '1px solid rgba(122,121,130,0.35)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(122,121,130,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiArchive size={18} color="#7A7982" />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>This asset is marked Redundant</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>New work orders are blocked and recurring PMs are skipped. History stays intact. Restore it to bring it back into the active list.</div>
          </div>
          <button onClick={async () => {
            try { await editAsset({ ...asset, status_id: 3 }); } catch (e) { /* surfaced by editAsset */ }
          }}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(60,128,44,0.5)', background: 'rgba(60,128,44,0.1)', color: '#3C802C', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Restore (mark Active)
          </button>
        </div>
      )}

      {/* QR Check-In Banner */}
      {qrBannerVisible && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: 16, padding: '14px 18px', borderRadius: 12, background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(16,185,129,0.08))', border: '1px solid rgba(37,99,235,0.25)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', boxShadow: '0 2px 8px rgba(37,99,235,0.08)' }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiCheckCircle size={20} color="#16a34a" />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Checked in via QR scan</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {issues.filter(i => i.status !== 'Complete' && i.status !== 'TECO').length} open work order{issues.filter(i => i.status !== 'Complete' && i.status !== 'TECO').length !== 1 ? 's' : ''} on this asset
            </div>
          </div>
          {Number(asset.status_id) !== 4 && (
            <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowCreateModal(true)}
              style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}>
              <FiPlus size={14} /> Create Notification
            </motion.button>
          )}
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => { setActiveTab('workorders'); setQrBannerVisible(false); }}
            style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiTool size={13} /> View Work Orders
          </motion.button>
          <button onClick={() => setQrBannerVisible(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, flexShrink: 0 }}><FiX size={18} /></button>
        </motion.div>
      )}

      {/* Recent Check-Ins */}
      {checkins.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', flexShrink: 0 }}>Recent Check-Ins</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
            {checkins.slice(0, 5).map(c => (
              <div key={c.id} title={`${c.user_name || 'Unknown'} — ${new Date(c.created_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 14, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: c.avatar_color || '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700 }}>
                  {(c.user_name || '?').split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)' }}>{c.user_name || 'Unknown'}</span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{new Date(c.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--bg-card)', borderRadius: 12, padding: 4, border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
        {[
          { key: 'info', label: 'Asset Info', icon: FiBox },
          { key: 'workorders', label: `Work Orders (${issues.length})`, icon: FiTool },
          { key: 'files', label: `Documents (${assetFileCount})`, icon: FiPaperclip },
          { key: 'licenses', label: `Licenses (${licenses.length})`, icon: FiKey }
        ].map(tab => {
          const active = activeTab === tab.key;
          return (
            <motion.button key={tab.key} whileTap={{ scale: 0.97 }} onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '12px 16px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
                background: active ? 'var(--accent)' : 'transparent',
                color: active ? '#fff' : 'var(--text-secondary)',
                boxShadow: active ? '0 4px 12px rgba(37,99,235,0.3)' : 'none'
              }}>
              <tab.icon size={16} /> {tab.label}
            </motion.button>
          );
        })}
      </motion.div>

      {/* Asset Info Tab */}
      {activeTab === 'info' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Asset Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))', gap: 16 }}>
            {asset.manufacturer_name && <InfoItem icon={FiBox} label="Manufacturer" value={asset.manufacturer_name} />}
            {asset.model_name && <InfoItem icon={FiBox} label="Model" value={asset.model_name} />}
            {asset.functional_location_code && <InfoItem icon={FiMapPin} label="Functional Location" value={`${asset.functional_location_asset_code || asset.functional_location_code} — ${asset.functional_location_name || ''}`.trim().replace(/—\s*$/, '').trim()} color="#8b5cf6" />}
            {asset.supplier_name && <InfoItem icon={FiBox} label="Supplier" value={asset.supplier_name} />}
            {asset.serial_number && <InfoItem icon={FiHash} label="Serial Number" value={asset.serial_number} mono />}
            {asset.purchase_date && asset.purchase_date !== '1970-01-01' && asset.purchase_date !== '' && <InfoItem icon={FiCalendar} label="Purchase Date" value={formatDateAU(asset.purchase_date)} />}
            {asset.warranty_end && asset.warranty_end !== '1970-01-01' && asset.warranty_end !== '' && <InfoItem icon={FiShield} label="Warranty End" value={formatDateAU(asset.warranty_end)} />}
            {customFields.map((f, i) => <InfoItem key={i} icon={FiBox} label={f.label} value={f.value} />)}
          </div>
          {asset.notes && stripHtml(asset.notes) && (
            <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 10, background: 'rgba(37,99,235,0.04)', border: '1px solid rgba(37,99,235,0.1)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{formatNotes(stripHtml(asset.notes))}</div>
            </div>
          )}
          {/* Sub-assets panel — lists direct children (caravan → generator,
              water pump, awning). Only rendered when this asset has at
              least one child. Click a row to open the child asset. */}
          {childAssets.length > 0 && (
            <div style={{ marginTop: 20, background: 'var(--bg-primary)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(139,92,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiBox size={16} color="#8b5cf6" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Sub-assets</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{childAssets.length} direct child{childAssets.length === 1 ? '' : 'ren'} attached to this asset</div>
                </div>
              </div>
              <div>
                {childAssets.map(c => (
                  <div key={c.id} onClick={() => navigate(`/assets/${c.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.06)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: '#8b5cf6', padding: '2px 8px', borderRadius: 4, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)' }}>{c.tag}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                    {c.child_asset_count > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}>+{c.child_asset_count}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Cost Breakdown — shown under Asset Info only */}
          {(assetTotalTime?.total_minutes > 0 || assetCosts?.total_cost > 0) && (
            <div style={{ marginTop: 20, background: 'var(--bg-primary)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(5,150,105,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiDollarSign size={16} color="#059669" />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Cost Breakdown</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 0 }}>
                {assetTotalTime?.total_minutes > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#2563eb' }}>{assetTotalTime.hours}h {assetTotalTime.minutes}m</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total Time</div>
                  </div>
                )}
                {assetCosts?.total_cost > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#059669' }}>${assetCosts.total_cost.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Lifetime Cost</div>
                  </div>
                )}
                {assetCosts?.labour_cost > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#059669' }}>${assetCosts.labour_cost.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Labour</div>
                  </div>
                )}
                {assetCosts?.material_cost > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#f59e0b' }}>${assetCosts.material_cost.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Parts</div>
                  </div>
                )}
                {assetCosts?.services > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0ea5e9' }}>${assetCosts.services.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Services</div>
                  </div>
                )}
                {assetCosts?.ext_labour > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#8b5cf6' }}>${assetCosts.ext_labour.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Ext. Labour</div>
                  </div>
                )}
              </div>
              {(assetCosts?.by_type || []).length > 1 && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '10px 16px', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {(assetCosts.by_type || []).map(bt => (
                    <div key={bt.type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text-muted)' }}>{bt.type}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>${bt.total.toLocaleString()}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{bt.hours}h</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Files Tab */}
      {activeTab === 'files' && (
        <AssetFilesTab assetId={asset.id} authFetch={authFetch} token={token} />
      )}

      {/* Licenses Tab */}
      {activeTab === 'licenses' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '24px', border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, color: 'var(--text-primary)' }}>Software Licenses</h3>

          {licenses.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
              No licenses attached
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {licenses.map(lic => (
              <div key={lic.id} style={{
                padding: '16px', borderRadius: 12, background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FiKey size={16} color="#2563eb" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lic.name}</div>
                    {lic.vendor && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{lic.vendor}</div>}
                  </div>
                </div>
                {lic.license_key && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <code style={{
                      fontSize: 12, fontFamily: 'monospace', padding: '4px 8px', borderRadius: 6,
                      background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-primary)',
                      cursor: 'pointer', userSelect: 'all', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}
                      onClick={() => setRevealedKeys(prev => {
                        const next = new Set(prev);
                        next.has(lic.id) ? next.delete(lic.id) : next.add(lic.id);
                        return next;
                      })}
                    >
                      {revealedKeys.has(lic.id) ? lic.license_key : maskKey(lic.license_key)}
                    </code>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>
                      {revealedKeys.has(lic.id) ? 'hide' : 'reveal'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Work Orders Tab */}
      {activeTab === 'workorders' && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {false && (assetTotalTime?.total_minutes > 0 || assetCosts?.total_cost > 0) && (
            <div style={{ marginBottom: 20, background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 0 }}>
                {assetTotalTime?.total_minutes > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#2563eb' }}>{assetTotalTime.hours}h {assetTotalTime.minutes}m</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total Time</div>
                  </div>
                )}
                {assetCosts?.total_cost > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#059669' }}>${assetCosts.total_cost.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Lifetime Cost</div>
                  </div>
                )}
                {assetCosts?.labour_cost > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#059669' }}>${assetCosts.labour_cost.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Labour</div>
                  </div>
                )}
                {assetCosts?.material_cost > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#f59e0b' }}>${assetCosts.material_cost.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Parts</div>
                  </div>
                )}
                {assetCosts?.services > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0ea5e9' }}>${assetCosts.services.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Services</div>
                  </div>
                )}
                {assetCosts?.ext_labour > 0 && (
                  <div style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#8b5cf6' }}>${assetCosts.ext_labour.toLocaleString()}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Ext. Labour</div>
                  </div>
                )}
              </div>
              {/* Charts row */}
              {assetCosts?.total_cost > 0 && (assetCosts.breakdown?.length > 0 || assetCosts.trend?.length > 0) && (
                <div style={{ display: 'grid', gridTemplateColumns: assetCosts.breakdown?.length > 0 && assetCosts.trend?.length > 0 ? '1fr 1fr' : '1fr', borderTop: '1px solid var(--border)' }}>
                  {assetCosts.breakdown?.length > 0 && (
                    <div style={{ padding: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Cost Breakdown</div>
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={assetCosts.breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={55} innerRadius={30}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {assetCosts.breakdown.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                          <Tooltip formatter={v => `$${v.toLocaleString()}`} contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {assetCosts.trend?.length > 0 && (
                    <div style={{ padding: 16, borderLeft: assetCosts.breakdown?.length > 0 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Monthly Trend (12m)</div>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={assetCosts.trend}>
                          <XAxis dataKey="month" tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 9, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                          <Tooltip formatter={v => `$${v.toLocaleString()}`} contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                          <Bar dataKey="labour" radius={[3, 3, 0, 0]} fill="#059669" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
              {/* By WO type breakdown */}
              {(assetCosts?.by_type || []).length > 1 && (
                <div style={{ borderTop: '1px solid var(--border)', padding: '10px 16px', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {(assetCosts.by_type || []).map(bt => (
                    <div key={bt.type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'var(--bg-input)', color: 'var(--text-muted)' }}>{bt.type}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>${bt.total.toLocaleString()}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{bt.hours}h</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Utilization card */}
          {assetUtilization && assetUtilization.total_hours > 0 && (
            <div style={{ marginBottom: 20, background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(37,99,235,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiClock size={16} color="#2563eb" />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Utilization</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 0 }}>
                <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#2563eb' }}>{assetUtilization.total_hours}h</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Lifetime Hours</div>
                </div>
                <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#8b5cf6' }}>{assetUtilization.total_jobs}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total Jobs</div>
                </div>
                <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#f59e0b' }}>{assetUtilization.this_month?.hours || 0}h</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>This Month</div>
                </div>
                <div style={{ padding: '14px 16px', textAlign: 'center', borderRight: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>${assetUtilization.cost_per_hour}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Cost / Hour</div>
                </div>
                <div style={{ padding: '14px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0ea5e9' }}>{assetUtilization.avg_jobs_per_month}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Avg Jobs / Month</div>
                </div>
              </div>
              {assetUtilization.months?.length > 0 && (
                <div style={{ padding: 16, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>12-Month Usage (Hours)</div>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={assetUtilization.months}>
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false}
                        tickFormatter={v => { const s = String(v); const m = s.match(/^(\d{4})-(\d{2})$/); return m ? `${m[2]}/${m[1]}` : s; }} />
                      <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}h`} />
                      <Tooltip formatter={v => `${v}h`} labelFormatter={v => { const s = String(v); const m = s.match(/^(\d{4})-(\d{2})$/); return m ? `${m[2]}/${m[1]}` : s; }}
                        contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                      <Bar dataKey="hours" radius={[3, 3, 0, 0]} fill="#2563eb" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Total: {issues.length}</span>
              {['All', 'PM01', 'PM02', 'PM03'].map(t => {
                const active = woTypeFilter === t;
                return (
                  <button key={t} onClick={() => setWoTypeFilter(t)} style={{
                    padding: '5px 12px', borderRadius: 6, border: active ? 'none' : '1px solid var(--border)',
                    background: active ? (t === 'All' ? 'var(--accent)' : { PM01: '#2563eb', PM02: '#0ea5e9', PM03: '#f59e0b' }[t]) : 'var(--bg-card)',
                    color: active ? '#fff' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                  }}>{t}</button>
                );
              })}
            </div>
            {(asset.status_name || '').toLowerCase() !== 'redundant' && (
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowCreateModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>
              <FiPlus size={16} /> New Notification</motion.button>
            )}
          </div>
          <div style={{ marginBottom: 16 }}>
            <input value={woSearch} onChange={e => setWoSearch(e.target.value)} placeholder="Search by WO number or name..."
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>

          {(() => {
            const filtered = issues.filter(i => {
              if (woTypeFilter !== 'All' && i.type !== woTypeFilter) return false;
              if (woSearch.trim()) {
                const q = woSearch.trim().toLowerCase();
                if (!(i.wo_number || '').toLowerCase().includes(q) && !(i.name || '').toLowerCase().includes(q)) return false;
              }
              return true;
            });
            return filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', boxShadow: 'var(--shadow)' }}>
                <FiTool size={36} style={{ marginBottom: 12, opacity: 0.4 }} /><p style={{ fontSize: 14 }}>{issues.length === 0 ? 'No work orders for this asset' : 'No matching work orders'}</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filtered.map(issue => {
                  const sc2 = statusColors[issue.status] || statusColors['New'];
                  return (
                    <motion.div key={issue.id} whileHover={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                      onClick={() => navigate(`/work-orders/${issue.wo_number}`)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
                        background: 'var(--bg-card)', borderRadius: 10, cursor: 'pointer',
                        border: '1px solid var(--border)', transition: 'all 0.15s', flexWrap: 'wrap'
                      }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, minWidth: 90 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                          {issue.wo_number || `WO-${issue.id}`}
                        </span>
                        <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: { PM01: '#2563eb', PM02: '#0ea5e9', PM03: '#f59e0b' }[issue.type] || '#6b7280' }}>{issue.type}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{issue.name}</div>
                        {issue.due_date && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Due: {(() => { const d = new Date(issue.due_date); return isNaN(d.getTime()) ? issue.due_date : `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</div>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc2.bg, color: sc2.color }}>{issue.status}</span>
                        <span style={{ padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, color: priorityColors[issue.priority] || '#64748b' }}>{issue.priority}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            );
          })()}
        </motion.div>
      )}
    </div>
  );
}

function InfoItem({ icon: Icon, label, value, color, mono }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: color ? `${color}15` : 'rgba(37,99,235,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={14} color={color || 'var(--accent)'} /></div>
      <div><div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit', color: 'var(--text-primary)' }}>{value}</div></div>
    </div>
  );
}
